import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { FaYoutube, FaFacebookF, FaXTwitter } from 'react-icons/fa6'
import Ornament from './Ornament'

const SOCIALS = [
    { label: 'YouTube', href: 'https://youtube.com/@centreforstudyofpoliticsan3585?si=JqRwTdkSVLE3FKTI', Icon: FaYoutube },
    { label: 'Facebook', href: 'https://www.facebook.com/share/194FKXQKSi/', Icon: FaFacebookF },
    { label: 'X (Twitter)', href: 'https://x.com/CSPGIndia', Icon: FaXTwitter }
]

export default function SiteFooter({ flush = false }: { flush?: boolean }) {
    return (
        <footer
            className={flush ? '' : 'mt-24'}
            style={{ background: 'var(--color-gold-900)', color: 'var(--color-paper-200)' }}
        >
            <hr className="rule-double rule-double--light" />
            <div className="container-prose pt-16 pb-10">
                <div className="flex flex-col items-center text-center">
                    <div
                        className="inline-flex items-center justify-center"
                        style={{
                            padding: '0.6rem 1.25rem',
                            borderRadius: '10px',
                            background:
                                'radial-gradient(ellipse at center, rgba(250,246,236,0.96) 0%, rgba(250,246,236,0.9) 55%, rgba(250,246,236,0) 100%)'
                        }}
                    >
                        <img
                            src="/brand/cspg-logo.png"
                            alt="Centre for Study of Politics and Governance"
                            style={{ height: '2.8rem', width: 'auto', display: 'block' }}
                        />
                    </div>
                    <Ornament tone="light" className="mt-7" />
                    <div
                        className="mt-6 font-display"
                        style={{
                            fontSize: 'clamp(1.6rem, 3vw, 2.25rem)',
                            color: 'var(--color-paper-100)',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.1
                        }}
                    >
                        <span style={{ color: 'var(--color-gold-300)' }}>Indian</span> Studies Review
                    </div>
                    <div
                        className="mt-3 text-[11px] uppercase font-medium"
                        style={{ letterSpacing: '0.28em', color: 'rgba(243, 236, 217, 0.6)' }}
                    >
                        A publication of CSPG India · since 2019
                    </div>
                </div>
            </div>

            <div
                style={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    borderBottom: '1px solid rgba(255,255,255,0.08)'
                }}
            >
                <div className="container-prose py-12 grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <FooterHeading>The Centre</FooterHeading>
                        <p className="mt-4 text-[14px] leading-relaxed" style={{ color: 'rgba(243, 236, 217, 0.7)' }}>
                            The Centre for Study of Politics and Governance is an independent research and policy
                            institute advancing rigorous, India-rooted political scholarship.
                        </p>
                        <address
                            className="mt-5 not-italic text-[13px] leading-relaxed"
                            style={{ color: 'rgba(243, 236, 217, 0.65)' }}
                        >
                            Room No-220, Department of Political Science<br />
                            University of Delhi, Delhi-110007, India<br />
                            <a
                                href="mailto:cspgindia@gmail.com"
                                className="hover:text-[var(--color-gold-300)]"
                                style={{ color: 'var(--color-paper-100)' }}
                            >
                                cspgindia@gmail.com
                            </a>
                        </address>
                        <div className="mt-7 flex gap-3">
                            {SOCIALS.map((s) => (
                                <SocialLink key={s.label} href={s.href} label={s.label}>
                                    <s.Icon size={15} />
                                </SocialLink>
                            ))}
                        </div>
                    </div>

                    <div className="md:col-span-3 md:col-start-7">
                        <FooterHeading>Journal</FooterHeading>
                        <FooterList
                            items={[
                                ['About the Journal', '/about-the-journal'],
                                ['Particulars', '/journal-particulars'],
                                ['Editorial Board', '/editorial-board'],
                                ['Editorial Workflow', '/editorial-workflow'],
                                ['Current Issue', '/current-issue'],
                                ['Archives', '/archives']
                            ]}
                        />
                    </div>

                    <div className="md:col-span-3">
                        <FooterHeading>Authors</FooterHeading>
                        <FooterList
                            items={[
                                ['Submission', '/submission'],
                                ['Guidelines', '/guidelines'],
                                ['Instructions', '/instructions'],
                                ['Plagiarism & AI', '/plagiarism-ai-policy'],
                                ['APC', '/article-processing-charge'],
                                ['FAQs', '/faqs']
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className="container-prose py-6 flex flex-col md:flex-row gap-3 md:items-center justify-between text-[11px] uppercase" style={{ letterSpacing: '0.2em', color: 'rgba(243, 236, 217, 0.45)' }}>
                <div>© {new Date().getFullYear()} CSPG India. All rights reserved.</div>
                <div>
                    Designed and built by{' '}
                    <a
                        href="https://www.albero.in/"
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold transition-colors hover:text-[var(--color-gold-300)]"
                        style={{ color: 'rgba(243, 236, 217, 0.75)' }}
                    >
                        Albero Technologies
                    </a>
                </div>
            </div>
        </footer>
    )
}

function FooterHeading({ children }: { children: ReactNode }) {
    return (
        <div
            className="text-[11px] uppercase font-semibold"
            style={{
                letterSpacing: '0.24em',
                color: 'var(--color-gold-300)',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                paddingBottom: '0.65rem'
            }}
        >
            {children}
        </div>
    )
}

function FooterList({ items }: { items: [string, string][] }) {
    return (
        <ul className="mt-4 space-y-2.5 text-[14px]">
            {items.map(([label, to]) => (
                <li key={to}>
                    <Link
                        to={to}
                        className="hover:text-[var(--color-gold-300)]"
                        style={{ color: 'rgba(243, 236, 217, 0.7)' }}
                    >
                        {label}
                    </Link>
                </li>
            ))}
        </ul>
    )
}

function SocialLink({ href, label, children }: { href: string; label: string; children: ReactNode }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            className="w-9 h-9 inline-flex items-center justify-center transition-all"
            style={{
                border: '1px solid rgba(255,255,255,0.16)',
                color: 'rgba(243,236,217,0.85)',
                borderRadius: '2px'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--color-gold-600)'
                e.currentTarget.style.borderColor = 'var(--color-gold-600)'
                e.currentTarget.style.color = '#fff'
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'
                e.currentTarget.style.color = 'rgba(243,236,217,0.85)'
            }}
        >
            {children}
        </a>
    )
}
